// stores
import * as ConceptValueStore from '../../../../redux/middleware/ProtocolConsumptionConceptValues.Middleware';

// state
import { AppState } from '../../../../redux/reducers';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import { ProtocolConsumption } from '../../../../models/Entities/Protocols/ProtocolConsumption';
import { ProtocolConsumptionConceptValues } from '../../../../models/Entities/Protocols/ProtocolConsumptionConceptValues';
import { NewProtocolConsumptionConceptValue } from '../../../../models/Entities/Protocols/NewProtocolConsumptionConceptValue';
import { ErrorType } from '../../../../models/HttpError';

//#region Props

interface IProps {
    ProtocolId: number;
    ProtocolConsumptionId: string;
    /** Necesario para elegir el concepto; sin contrato no hay de donde elegirlo. */
    ContractId: number | undefined;
    /** undefined = alta; con valor = edicion de esa fila. */
    ConceptValue: ProtocolConsumptionConceptValues | undefined;
    isOpen: boolean;
    onClose: () => void;
}

interface ConnectedProps {
    Consumption: ProtocolConsumption | null;
    isSaving: boolean;
    saved: boolean | undefined;
    failOnSaving: boolean;
    errorOnSaving: ErrorType | undefined;
}

interface DispatchProps {
    CreateConceptValue: typeof ConceptValueStore.ActionCreators.CreateConceptValue;
    UpdateConceptValue: typeof ConceptValueStore.ActionCreators.UpdateConceptValue;
}

export type Props = IProps & ConnectedProps & DispatchProps;

//#endregion Props

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const auxProtocol: Protocol | null =
        state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId) || null;

    const auxConsumption: ProtocolConsumption | null =
        // lista plana (donde deja el consumo GetProtocolConsumptionById)
        auxProtocol?.consumptions?.find(
            (consumption: ProtocolConsumption) => consumption.id === ownProps.ProtocolConsumptionId
        ) ??
        // consumos anidados en el padre, cuando el detalle se abre desde un agrupado
        auxProtocol?.consumptions?.flatMap(
            (consumption: ProtocolConsumption) => consumption.childConsumption ?? []
        ).find(
            (consumption: ProtocolConsumption) => consumption.id === ownProps.ProtocolConsumptionId
        ) ?? null;

    return {
        Consumption: auxConsumption,
        isSaving: auxConsumption?.isSavingConceptValue ?? false,
        saved: auxConsumption?.conceptValueSaved,
        failOnSaving: auxConsumption?.failOnSavingConceptValue ?? false,
        errorOnSaving: auxConsumption?.ErrorOnSavingConceptValue
    };
};

export const mapDispatchToProps = {
    ...ConceptValueStore.ActionCreators
};

//#region Draft & calculation

/**
 * Lo que el formulario edita. Todo lo numerico viaja como texto porque un input
 * a medio tipear ("12,", "-") no es un numero y forzarlo a serlo hace saltar el
 * cursor mientras se escribe.
 */
export interface ConceptValueDraft {
    contractConceptId: number | undefined;
    contractConceptName: string;
    /** 'percent': el importe sale de un % del consumo. 'amount': se escribe a mano. */
    calculationMode: 'percent' | 'amount';
    percentValue: string;
    value: string;
    currencyCode: string;
    haveDivisionFee: boolean;
    feeDivisionPercentage: string;
    teamDivisionPercentage: string;
    applyWithholding: boolean;
    withholdingPercentage: string;
}

export const EmptyDraft = (currencyCode: string): ConceptValueDraft => ({
    contractConceptId: undefined,
    contractConceptName: '',
    calculationMode: 'amount',
    percentValue: '',
    value: '',
    currencyCode: currencyCode,
    haveDivisionFee: false,
    feeDivisionPercentage: '100',
    teamDivisionPercentage: '0',
    applyWithholding: false,
    withholdingPercentage: ''
});

/** Pasa una fila existente al formato editable del formulario. */
export const DraftFromConceptValue = (
    conceptValue: ProtocolConsumptionConceptValues,
    fallbackCurrency: string
): ConceptValueDraft => ({
    contractConceptId: conceptValue.contractConceptId,
    contractConceptName: conceptValue.contractConceptName ?? '',
    calculationMode: conceptValue.percentValue ? 'percent' : 'amount',
    percentValue: conceptValue.percentValue ? String(conceptValue.percentValue) : '',
    value: conceptValue.value !== undefined && conceptValue.value !== null ? String(conceptValue.value) : '',
    currencyCode: conceptValue.currencyCode || fallbackCurrency,
    haveDivisionFee: conceptValue.haveDivisionFee ?? false,
    feeDivisionPercentage: String(conceptValue.feeDivisionPercentage ?? 100),
    teamDivisionPercentage: String(conceptValue.teamDivisionPercentage ?? 0),
    applyWithholding: conceptValue.applyWithholding ?? false,
    withholdingPercentage: conceptValue.withholdingPercentage ? String(conceptValue.withholdingPercentage) : ''
});

/** Acepta coma decimal, que es como se tipea en es-AR. */
export const ParseDecimal = (text: string): number | null => {
    const normalized = (text ?? '').trim().replace(',', '.');

    if (!normalized.length) return null;

    const parsed = Number(normalized);

    return Number.isFinite(parsed) ? parsed : null;
};

const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export interface ConceptValueAmounts {
    value: number;
    percentValue: number | undefined;
    ownerAmount: number;
    teamAmount: number;
    withholdingAmount: number;
    ownnerWithholdingAmount: number;
    teamWithholdingAmount: number;
    finalOwnerAmount: number;
    finalTeamAmount: number;
}

/**
 * Deriva todos los importes de la fila. El servidor no recalcula nada: recibe
 * los numeros ya resueltos, asi que esta funcion es la definicion efectiva de
 * como se reparte un concepto adicional. Espeja lo que la grilla de valores ya
 * muestra: neto = bruto − retencion, por cada parte.
 */
export const ComputeConceptValueAmounts = (
    draft: ConceptValueDraft,
    consumptionAmount: number
): ConceptValueAmounts => {

    const percent = draft.calculationMode === 'percent' ? ParseDecimal(draft.percentValue) : null;

    const value = draft.calculationMode === 'percent'
        ? round2((consumptionAmount * (percent ?? 0)) / 100)
        : round2(ParseDecimal(draft.value) ?? 0);

    const feePercentage = draft.haveDivisionFee ? (ParseDecimal(draft.feeDivisionPercentage) ?? 0) : 100;
    const teamPercentage = draft.haveDivisionFee ? (ParseDecimal(draft.teamDivisionPercentage) ?? 0) : 0;

    const ownerAmount = round2((value * feePercentage) / 100);
    const teamAmount = round2((value * teamPercentage) / 100);

    const withholdingPercentage = draft.applyWithholding ? (ParseDecimal(draft.withholdingPercentage) ?? 0) : 0;

    const withholdingAmount = round2((value * withholdingPercentage) / 100);
    const ownnerWithholdingAmount = round2((ownerAmount * withholdingPercentage) / 100);
    const teamWithholdingAmount = round2((teamAmount * withholdingPercentage) / 100);

    return {
        value,
        percentValue: draft.calculationMode === 'percent' ? (percent ?? 0) : undefined,
        ownerAmount,
        teamAmount,
        withholdingAmount,
        ownnerWithholdingAmount,
        teamWithholdingAmount,
        finalOwnerAmount: round2(ownerAmount - ownnerWithholdingAmount),
        finalTeamAmount: round2(teamAmount - teamWithholdingAmount)
    };
};

/** Devuelve los mensajes a mostrar; vacio significa que se puede guardar. */
/**
 * Devuelve claves de i18n, no textos: la validacion vive fuera del componente
 * y no tiene acceso a `t`. La vista traduce cada clave al renderizar.
 */
export const ValidateDraft = (draft: ConceptValueDraft): string[] => {
    const errors: string[] = [];

    if (!draft.contractConceptId) {
        errors.push('conceptValueForm.validation.concept');
    }

    if (draft.calculationMode === 'percent') {
        const percent = ParseDecimal(draft.percentValue);
        if (percent === null) {
            errors.push('conceptValueForm.validation.percentRequired');
        } else if (percent <= 0) {
            errors.push('conceptValueForm.validation.percentPositive');
        }
    } else {
        const value = ParseDecimal(draft.value);
        if (value === null) {
            errors.push('conceptValueForm.validation.amountRequired');
        } else if (value === 0) {
            errors.push('conceptValueForm.validation.amountNonZero');
        }
    }

    if (!draft.currencyCode) {
        errors.push('conceptValueForm.validation.currency');
    }

    if (draft.haveDivisionFee) {
        const fee = ParseDecimal(draft.feeDivisionPercentage);
        const team = ParseDecimal(draft.teamDivisionPercentage);

        if (fee === null || team === null) {
            errors.push('conceptValueForm.validation.divisionIncomplete');
        } else if (round2(fee + team) !== 100) {
            errors.push('conceptValueForm.validation.divisionSum');
        }
    }

    if (draft.applyWithholding) {
        const withholding = ParseDecimal(draft.withholdingPercentage);
        if (withholding === null) {
            errors.push('conceptValueForm.validation.withholdingRequired');
        } else if (withholding < 0 || withholding > 100) {
            errors.push('conceptValueForm.validation.withholdingRange');
        }
    }

    return errors;
};

/** Arma el comando que espera la API a partir del borrador ya validado. */
export const BuildCommand = (
    draft: ConceptValueDraft,
    consumptionId: string,
    consumptionAmount: number
): NewProtocolConsumptionConceptValue => {

    const amounts = ComputeConceptValueAmounts(draft, consumptionAmount);

    return {
        protocolConsumedServiceId: consumptionId,
        contractConceptId: draft.contractConceptId as number,
        contractConceptName: draft.contractConceptName,
        percentValue: amounts.percentValue,
        value: amounts.value,
        currencyCode: draft.currencyCode,
        haveDivisionFee: draft.haveDivisionFee,
        feeDivisionPercentage: draft.haveDivisionFee ? (ParseDecimal(draft.feeDivisionPercentage) ?? 0) : 100,
        ownerAmount: amounts.ownerAmount,
        teamDivisionPercentage: draft.haveDivisionFee ? (ParseDecimal(draft.teamDivisionPercentage) ?? 0) : 0,
        teamAmount: amounts.teamAmount,
        applyWithholding: draft.applyWithholding,
        withholdingPercentage: draft.applyWithholding ? (ParseDecimal(draft.withholdingPercentage) ?? 0) : 0,
        withholdingAmount: amounts.withholdingAmount,
        ownnerWithholdingAmount: amounts.ownnerWithholdingAmount,
        teamWithholdingAmount: amounts.teamWithholdingAmount,
        finalOwnerAmount: amounts.finalOwnerAmount,
        finalTeamAmount: amounts.finalTeamAmount
    };
};

//#endregion Draft & calculation
